import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCourts } from '../../../hooks/court/useCourts'
import { useBlock } from '../../../hooks/block/useBlock'

import Loading from '../../../components/loading/Loading'
import { Button } from '../../../components/buttons/Button'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import { BlockEdit } from '../../../components/authenticated/management/block/blockEdit/BlockEdit'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { BlockModel, CourtModel } from '../../../types/court/court'
import {
  createNewBlockFormValues,
  getCourtSchedule,
  toFormValues,
} from '../../../utils/block/blockUtils'

const BLOCKS_PATH = '/m/blocks'

export const ManagerBlock = () => {
  const navigate = useNavigate()
  const { blockId } = useParams()
  const [searchParams] = useSearchParams()
  const { showBanner } = useApp()
  const { t } = useTranslation('blocks')
  const [courtsList, setCourtsList] = useState<CourtModel[]>([])
  const [initialValues, setInitialValues] = useState<BlockModel | null>(null)

  const isEditMode = Boolean(blockId)

  const {
    courts,
    loading: courtsLoading,
    error: courtsError,
    resetState: resetCourtsState,
  } = useCourts()

  const {
    block,
    loading: blockLoading,
    error: blockError,
    resetState: resetBlockState,
  } = useBlock()

  const requestedCourtId = searchParams.get('courtId')
  const requestedStartDate = searchParams.get('startDate')
  const requestedEndDate = searchParams.get('endDate')
  const requestedStartTime = searchParams.get('startTime')
  const requestedEndTime = searchParams.get('endTime')

  useEffect(() => {
    let canceled = false

    const loadData = async () => {
      setInitialValues(null)

      const parsedBlockId = blockId ? Number(blockId) : null
      const parsedCourtId = requestedCourtId ? Number(requestedCourtId) : null

      if (
        parsedBlockId !== null &&
        (!Number.isInteger(parsedBlockId) ||
          parsedBlockId <= 0 ||
          parsedCourtId === null ||
          !Number.isInteger(parsedCourtId) ||
          parsedCourtId <= 0)
      ) {
        navigate(BLOCKS_PATH, { replace: true })
        return
      }

      const [courtsResponse, blockResponse] = await Promise.all([
        courts(),
        parsedBlockId !== null ? block(parsedCourtId!, parsedBlockId) : null,
      ])

      if (canceled) {
        return
      }

      const courtList = courtsResponse?.content ?? []
      setCourtsList(courtList)

      if (parsedBlockId !== null) {
        if (!blockResponse) {
          navigate(BLOCKS_PATH, { replace: true })
          return
        }

        setInitialValues(toFormValues(blockResponse))
        return
      }

      const selectedCourt = courtList.find(
        court => String(court.id) === requestedCourtId
      )

      if (requestedCourtId && selectedCourt?.id === undefined) {
        return
      }

      if (!selectedCourt || selectedCourt.id === undefined) {
        setInitialValues(createNewBlockFormValues(0))
        return
      }

      const { openTime, closeTime } = getCourtSchedule(selectedCourt)

      const schedule = requestedStartDate
        ? {
            startDate: requestedStartDate,
            endDate: requestedEndDate ?? requestedStartDate,
            startTime: requestedStartTime ?? openTime,
            endTime: requestedEndTime ?? closeTime,
          }
        : undefined

      setInitialValues(createNewBlockFormValues(selectedCourt.id, schedule))
    }

    void loadData()

    return () => {
      canceled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blockId,
    requestedCourtId,
    requestedStartDate,
    requestedEndDate,
    requestedStartTime,
    requestedEndTime,
  ])

  useEffect(() => {
    const error = courtsError || blockError

    if (!error) {
      return
    }

    showBanner({
      kind: BannerKind.ERR,
      msg: error,
    })

    resetState()
    navigate(BLOCKS_PATH, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtsError, blockError])

  const resetState = () => {
    resetCourtsState()
    resetBlockState()
  }

  const handleBack = () => {
    navigate(BLOCKS_PATH)
  }

  const pageLoading = courtsLoading || (isEditMode && blockLoading)
  const isLoadingData = pageLoading || (isEditMode && !initialValues)
  const hasNoCourts =
    !isLoadingData && !courtsError && courtsList.length === 0

  return (
    <>
      <PageHeader
        title={{
          eyebrow: t(
            isEditMode
              ? 'blocks.edit.page-header.eyebrow'
              : 'blocks.new.page-header.eyebrow'
          ),
          h1: t(
            isEditMode
              ? 'blocks.edit.page-header.title'
              : 'blocks.new.page-header.title'
          ),
        }}
        sub={t(
          isEditMode
            ? 'blocks.edit.page-header.subtitle'
            : 'blocks.new.page-header.subtitle'
        )}
        actions={[
          <Button
            key="back"
            label={t('blocks.edit.page-header.back-btn')}
            size="md"
            onClick={handleBack}
          />,
        ]}
      />

      {isLoadingData ? (
        <Loading variant="card" />
      ) : hasNoCourts ? (
        <div className="ph-card">{t('blocks.no-courts')}</div>
      ) : initialValues ? (
        <BlockEdit
          courts={courtsList}
          initialValues={initialValues}
          isEditMode={isEditMode}
          blockId={blockId ? Number(blockId) : undefined}
        />
      ) : null}
    </>
  )
}
